// „Задържане на поръчка" — чистите правила, общи за каталога (времето на ред),
// детайла на масата (срокът, „Промени", „Пусни сега", „Обобщено") и питането
// „Задържани поръчки" преди закриване. Договорът с BE — `api/billKitchenHold.ts`.

import type { BillElementOut, BillOut } from "../api/bills";

/** Минутите, които изборът на време предлага (BE приема 1..240). „Сега" —
 *  веднага; ∞ (ръчно) — `minutes: null`. */
export const KITCHEN_HOLD_MINUTE_OPTIONS = [5, 10, 15, 20, 30] as const;

/** Партидата още не е влязла в кухнята: задържана и неосвободена. Единственото
 *  място с това правило — не го преписвай наум. */
export const isKitchenHeld = (element: Pick<BillElementOut, "kitchen_hold">): boolean =>
  element.kitchen_hold != null && element.kitchen_hold.released_at == null;

/**
 * Задържаните партиди на сметката, които още имат редове — изцяло сторнирана
 * партида няма какво да пусне (BE само я маркира освободена), затова не се
 * брои и в питането преди закриване.
 */
export const heldElementsOf = (
  bill: Pick<BillOut, "elements"> | null | undefined,
): BillElementOut[] =>
  (bill?.elements ?? []).filter(
    (element) => element.items.length > 0 && isKitchenHeld(element),
  );

/**
 * Колко задържани поръчки засяга изборът на редове (`bill_element_items.id`) —
 * „Плати избраните" закрива новата сметка и BE ги пуска в кухнята.
 */
export const heldElementCountAmong = (
  bill: Pick<BillOut, "elements"> | null | undefined,
  itemIds: Iterable<number>,
): number => {
  const selected = new Set(itemIds);
  return heldElementsOf(bill).filter((element) =>
    element.items.some((item) => selected.has(item.id)),
  ).length;
};

/** Колко остава до `hold_until` при „сега" `nowMs` (часовникът на сървъра);
 *  null — ръчно задържане или неразчетим момент. */
export const kitchenHoldRemainingMs = (
  holdUntil: string | null | undefined,
  nowMs: number,
): number | null => {
  if (!holdUntil) return null;
  const untilMs = Date.parse(holdUntil);
  return Number.isFinite(untilMs) ? untilMs - nowMs : null;
};

/** Секундите, закръглени НАГОРЕ: 0 идва точно в момента на `hold_until`, не
 *  секунда по-рано. */
const remainingSeconds = (remainingMs: number) =>
  Math.max(0, Math.ceil(remainingMs / 1000));

/** Броячът стигна 0 — на екрана „Влиза…" (BE я пуска по своя таймер). */
export const isKitchenHoldExpired = (remainingMs: number): boolean =>
  remainingSeconds(remainingMs) === 0;

/** Цели минути до срока, НАГОРЕ — „след 29 мин" до последната секунда на
 *  29-ата минута, „след 1 мин" в последната; 10 мин задържане е „след 10 мин". */
export const kitchenHoldRemainingMinutes = (remainingMs: number): number =>
  Math.ceil(remainingSeconds(remainingMs) / 60);

/** Най-ранният `hold_until` сред задържаните партиди (ms); null — няма таймер
 *  (нищо задържано или само ръчни). */
export const nextKitchenHoldDeadlineMs = (
  elements: BillElementOut[],
): number | null => {
  let earliest: number | null = null;
  for (const element of elements) {
    if (!isKitchenHeld(element) || element.items.length === 0) continue;
    const untilMs = Date.parse(element.kitchen_hold?.hold_until ?? "");
    if (Number.isFinite(untilMs) && (earliest === null || untilMs < earliest))
      earliest = untilMs;
  }
  return earliest;
};
